import { useEffect } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { getRouteApi } from '@mint/routing';

import { useCampaignCurrencyMode } from './useCampaignCurrencyMode.js';
import { useGetBuilderTableViewData } from './useGetBuilderTableViewData.js';
import { useGetBuyingPlanInfoData } from './useGetBuyingPlanData.js';
import { useGetBuyingPlanListData } from './useGetBuyingPlanListData.js';
import { useGetBuyingPlanMetaData } from './useGetBuyingPlanMetaData.js';
import useGetPlatformIntegrations from './useGetPlatformIntegrations.js';
import {
  useSelectedBuyingPlanId,
  useSetSelectedBuyingPlanId,
} from './useSelectedBuyingPlanId.js';
import useSimpleNotification from './useSImpleNotification.js';

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan/',
);

const useInitiateBuilderQueries = () => {
  const { t } = useTranslation('@myn/planning');
  const search = routeApi.useSearch();
  const [isCampaignCurrencyMode] = useCampaignCurrencyMode();
  const selectedPlanId = useSelectedBuyingPlanId();
  const setSelectedPlanId = useSetSelectedBuyingPlanId();

  const {
    isLoading: isBuilderDataLoading,
    isError: isBuilderDataError,
    data: builderViewData,
  } = useGetBuilderTableViewData();

  const {
    data: mediaPlans,
    isLoading: isMediaPlansLoading,
    isError: isMediaPlansError,
  } = useGetBuyingPlanListData({ skip: !builderViewData?.id });

  const idFromUrl = mediaPlans?.find(
    (plan) => plan.id === Number(search.mediaplan),
  )?.id;
  const lastMediaPlanId = mediaPlans?.[mediaPlans.length - 1]?.id;
  const mediaPlanId = idFromUrl || selectedPlanId || lastMediaPlanId || null;
  const includeFields = builderViewData?.columns.map((column) => column.name);

  const {
    isLoading: isMediaPlanLoading,
    isError: isMediaPlanError,
    data: mediaPlanInfo,
  } = useGetBuyingPlanInfoData(
    {
      buyingPlanId: mediaPlanId,
      includeFields: includeFields ?? [],
      multiCurrency: !isCampaignCurrencyMode,
    },
    {
      skip: !mediaPlanId || !includeFields,
    },
  );

  const {
    isLoading: isMediaPlanMetaLoading,
    isError: isMediaPlanMetaError,
    data: mediaPlanMeta,
  } = useGetBuyingPlanMetaData(mediaPlanId);

  const { isLoading: isPlatformIntegrationsLoading } =
    useGetPlatformIntegrations();

  useEffect(() => {
    if (mediaPlanId !== selectedPlanId) {
      setSelectedPlanId(mediaPlanId);
    }
  }, [mediaPlanId, selectedPlanId]);

  const { showErrorNotification } = useSimpleNotification();

  const isBuilderHeaderLoading =
    isBuilderDataLoading || isMediaPlansLoading || isMediaPlanLoading;
  const isBuilderBodyLoading =
    isBuilderHeaderLoading ||
    isMediaPlanMetaLoading ||
    isPlatformIntegrationsLoading;
  const isAvailableMediaPlans = !!mediaPlans && mediaPlans.length > 0;

  const isFirstLoadingError =
    isMediaPlansError ||
    (isMediaPlanError && !mediaPlanInfo) ||
    (isBuilderDataError && !builderViewData) ||
    (isMediaPlanMetaError && !mediaPlanMeta);

  const isNonFirstLoadingError =
    (isMediaPlanError && mediaPlanInfo) ||
    (isBuilderDataError && builderViewData) ||
    (isMediaPlanMetaError && mediaPlanMeta);

  useEffect(() => {
    if (isFirstLoadingError) {
      throw new Error('Error while loading crucial data');
    }

    if (isNonFirstLoadingError) {
      showErrorNotification({
        title: t('Error'),
        subTitle: t('Latest data is not available. Please try again'),
      });
    }
  }, [isFirstLoadingError, isNonFirstLoadingError]);

  return {
    isBuilderHeaderLoading,
    isBuilderBodyLoading,
    isAvailableMediaPlans,
  };
};

export default useInitiateBuilderQueries;
